import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import {
  SEAT_BAND_LABELS,
  type UsageSeatBands,
} from "@/v2-backoffice-client/usage/scope/usage.scope.utils"

export type UsageSeatBarProps = ComponentProps<"div"> & {
  bands: UsageSeatBands
  /** Also render the per-band legend under the bar. */
  showLegend?: boolean
}

const SEGMENTS: {
  key: keyof UsageSeatBands
  label: string
  className: string
}[] = [
  {
    key: "power",
    label: SEAT_BAND_LABELS.power,
    className: "usage-seat-segment-power",
  },
  {
    key: "healthy",
    label: SEAT_BAND_LABELS.healthy,
    className: "usage-seat-segment-healthy",
  },
  {
    key: "cooling",
    label: SEAT_BAND_LABELS.cooling,
    className: "usage-seat-segment-cooling",
  },
  {
    key: "dormant",
    label: SEAT_BAND_LABELS.dormant,
    className: "usage-seat-segment-dormant",
  },
  {
    key: "quiet",
    label: SEAT_BAND_LABELS.quiet,
    className: "usage-seat-segment-quiet",
  },
]

// Stacked license bar: every seat is a slice, colored by how engaged that
// seat is. This is the picture behind the engagement-vs-churn paradox — a
// green sliver next to a long grey tail says "great users, idle licenses"
// at a glance.
function UsageSeatBar(props: UsageSeatBarProps) {
  const { bands, showLegend = false, className, ...divProps } = props

  const total = SEGMENTS.reduce((sum, segment) => sum + bands[segment.key], 0)
  if (total === 0) return null

  return (
    <div className={cn("flex flex-col gap-1.5", className)} {...divProps}>
      <div
        className="usage-seat-bar"
        role="img"
        aria-label="Seat engagement breakdown"
      >
        {SEGMENTS.map((segment) => {
          const count = bands[segment.key]
          if (count === 0) return null
          return (
            <div
              key={segment.key}
              className={cn("usage-seat-bar-segment", segment.className)}
              style={{ width: `${(count / total) * 100}%` }}
              title={`${segment.label}: ${count} of ${total} seats`}
            />
          )
        })}
      </div>
      {showLegend && (
        <div className="usage-seat-legend">
          {SEGMENTS.map((segment) => {
            const count = bands[segment.key]
            if (count === 0) return null
            return (
              <span
                key={segment.key}
                className="inline-flex items-center gap-1"
              >
                <span
                  className={cn("usage-seat-legend-swatch", segment.className)}
                />
                {count} {segment.label.toLowerCase()}
              </span>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default UsageSeatBar
